import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { MessageCircle, AlertCircle, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import Header from './Header';
import Footer from './Footer';
import FloatingWhatsApp from './FloatingWhatsApp';

const BlogCaldaiaErroreE01 = () => {
  const handleWhatsApp = () => {
    window.open('https://wa.me/393287686356?text=Ciao,%20la%20mia%20caldaia%20segna%20errore%20E01.%20Potete%20intervenire?', '_blank');
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Caldaia Errore E01: Cosa Fare | Guida Pratica SOS Casa Roma</title>
        <meta name="description" content="Caldaia segna errore E01? Scopri cause e soluzioni immediate. Assistenza caldaie H24 a Roma. Tel: 328 768 6356" />
        <link rel="canonical" href={`${window.location.origin}/blog/caldaia-errore-e01-cosa-fare`} />
      </Helmet>
      
      <div className="min-h-screen bg-gray-50">
        <Header />
        
        <article className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto bg-white rounded-lg shadow-lg p-8">
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Cosa Fare se la Caldaia Segna Errore E01
              </h1>
              
              <div className="prose max-w-none">
                <p className="text-lg text-gray-700 mb-6">
                  L'errore E01 sulla caldaia indica generalmente un problema di accensione o mancata rilevazione della fiamma. Ecco come intervenire subito.
                </p>

                <div className="bg-orange-50 border-l-4 border-orange-500 p-4 mb-6">
                  <div className="flex items-start">
                    <AlertCircle className="h-6 w-6 text-orange-500 mr-3 flex-shrink-0 mt-1" />
                    <p className="text-gray-800">
                      <strong>Sicurezza prima di tutto:</strong> Se senti odore di gas, chiudi subito il rubinetto gas, apri le finestre e chiama un tecnico.
                    </p>
                  </div>
                </div>

                <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
                  Cosa Significa Errore E01
                </h2>
                <p className="text-gray-700 mb-4">
                  L'errore E01 è un codice di blocco che appare quando:
                </p>
                <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
                  <li>La caldaia non riesce ad accendere la fiamma</li>
                  <li>Il rilevatore di fiamma non funziona correttamente</li>
                  <li>C'è un problema nell'alimentazione del gas</li>
                  <li>Gli elettrodi di accensione sono sporchi o danneggiati</li>
                </ul>

                <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
                  Soluzioni Immediate da Provare
                </h2>
                
                <div className="space-y-4 mb-6">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-700 text-white rounded-full flex items-center justify-center font-bold">1</div>
                    <div>
                      <p className="font-semibold text-gray-900">Reset della caldaia</p>
                      <p className="text-gray-700">Premi il tasto reset per 3-5 secondi e attendi che la caldaia tenti di riaccendersi.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-700 text-white rounded-full flex items-center justify-center font-bold">2</div>
                    <div>
                      <p className="font-semibold text-gray-900">Controlla il gas</p>
                      <p className="text-gray-700">Verifica che il rubinetto del gas sia completamente aperto e che altri fornelli funzionino.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-700 text-white rounded-full flex items-center justify-center font-bold">3</div>
                    <div>
                      <p className="font-semibold text-gray-900">Verifica la pressione</p>
                      <p className="text-gray-700">Controlla che la pressione dell'acqua sia tra 1 e 1,5 bar (zona verde).</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-700 text-white rounded-full flex items-center justify-center font-bold">4</div>
                    <div>
                      <p className="font-semibold text-gray-900">Attendi qualche minuto</p>
                      <p className="text-gray-700">Se l'errore persiste dopo il reset, spegni e riaccendi la caldaia dopo 5 minuti.</p>
                    </div>
                  </div>
                </div>

                <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-6">
                  <div className="flex items-start">
                    <CheckCircle className="h-6 w-6 text-green-500 mr-3 flex-shrink-0 mt-1" />
                    <p className="text-gray-800">
                      <strong>Nota:</strong> Se dopo 2-3 tentativi l'errore persiste, non continuare a insistere. Serve l'intervento di un tecnico specializzato.
                    </p>
                  </div>
                </div>

                <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
                  Cause Tecniche Errore E01
                </h2>
                <p className="text-gray-700 mb-4">
                  Le cause più comuni richiedono l'intervento di un tecnico:
                </p>
                <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
                  <li>Elettrodi di accensione incrostati o danneggiati</li>
                  <li>Valvola gas difettosa o bloccata</li>
                  <li>Rilevatore di fiamma (ionizzazione) guasto</li>
                  <li>Scheda elettronica danneggiata</li>
                  <li>Pressione gas insufficiente</li>
                </ul>

                <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
                  Quando Chiamare Subito un Tecnico
                </h2>
                <p className="text-gray-700 mb-4">
                  Contatta immediatamente SOS Casa Roma se:
                </p>
                <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
                  <li>Senti odore di gas</li>
                  <li>L'errore E01 compare ripetutamente</li>
                  <li>La caldaia non si resetta</li>
                  <li>Hai bisogno di acqua calda urgentemente</li>
                  <li>La caldaia ha più di 8-10 anni (potrebbe servire manutenzione straordinaria)</li>
                </ul>

                <div className="bg-blue-600 rounded-lg p-8 text-center mt-12">
                  <h3 className="text-2xl font-bold text-white mb-4">
                    Caldaia in Errore E01? Interveniamo Subito
                  </h3>
                  <p className="text-white text-lg mb-6">
                    Tecnici specializzati in tutte le marche. Arrivo garantito in 30-60 minuti a Roma. Assistenza H24 anche festivi.
                  </p>
                  <Button
                    onClick={handleWhatsApp}
                    size="lg"
                    className="bg-orange-500 hover:bg-orange-600 text-white text-xl px-10 py-7 rounded-lg shadow-lg transition-all duration-300 hover:scale-105"
                  >
                    <MessageCircle className="mr-3 h-7 w-7" />
                    Chiama SOS Casa Roma su WhatsApp
                  </Button>
                  <p className="text-white mt-4 font-semibold">
                    📞 328 768 6356
                  </p>
                </div>
              </div>
            </div>
          </div>
        </article>

        <Footer />
        <FloatingWhatsApp />
      </div>
    </>
  );
};

export default BlogCaldaiaErroreE01;
